import {
  type ComponentRequirementFlags,
  getComponentRequirements,
  isComponentAllowedOnPage,
  pickT,
} from "@repo/shared";
import type { FlowField } from "@repo/shared/types";
import type { ComponentType, FlowPageComponent, PageType } from "@/lib/types/flow";
import { textBlockHeading } from "@/lib/utils/text-block-content";
import { CATEGORY_LABELS, COMPONENT_TYPE_LABELS } from "./flow-component-types.i18n";

// Component categories for grouping in the picker
export type FlowComponentCategory = "input" | "portfolio" | "costs" | "client" | "formatting";

export function getFlowComponentCategories(
  lang: string,
): { id: FlowComponentCategory; label: string }[] {
  const labels = pickT(CATEGORY_LABELS, lang);
  return [
    { id: "input", label: labels.input },
    { id: "portfolio", label: labels.portfolio },
    { id: "costs", label: labels.costs },
    { id: "client", label: labels.client },
    { id: "formatting", label: labels.formatting },
  ];
}

// Component type metadata for the type selector.
// Requirement flags are sourced from the shared module (@repo/shared) via
// `ComponentRequirementFlags`, not hand-declared here — see Module A
// (packages/shared/src/flow-requirements.ts), the single source of truth.
export type FlowComponentTypeInfo = {
  type: ComponentType;
  label: string;
  description: string;
  detailedDescription: string;
  category: FlowComponentCategory;
  // Whether this component is only for goal pages
  goalOnly?: boolean;
  // Whether this component is only for global pages
  globalOnly?: boolean;
  // Whether this component requires X-Ray access (tenant feature, not a flow assignment)
  requiresXRay?: boolean;
} & ComponentRequirementFlags;

// Base component metadata — display/layout concerns only. Requirement flags are
// NOT declared here; they are merged in from the shared module in
// `getFlowComponentTypes`. `requiresXRay` stays local: it is a tenant feature/
// access concern, not a flow assignment requirement.
// Page-type placement is NOT declared here — it lives in the shared placement policy
// (@repo/shared `isComponentAllowedOnPage`), the single source of truth for both the editor
// and the runtime renderers (QP-150). `requiresXRay` stays local: a tenant feature/access
// concern, not a flow assignment requirement.
type ComponentMetadata = {
  type: ComponentType;
  category: FlowComponentCategory;
  requiresXRay?: boolean;
};

const COMPONENT_METADATA: ComponentMetadata[] = [
  // Input components
  { type: "field", category: "input" },
  { type: "goal_input", category: "input" },
  { type: "goal_list", category: "input" },
  { type: "cash_transfers", category: "input" },
  { type: "portfolio_input", category: "input" },
  { type: "custom_portfolio", category: "input" },
  // Portfolio components
  { type: "portfolio_display", category: "portfolio" },
  {
    type: "portfolio_xray",
    category: "portfolio",
    requiresXRay: true,
  },
  { type: "asset_allocation", category: "portfolio" },
  { type: "portfolio_forecast", category: "portfolio" },
  { type: "historical_return", category: "portfolio" },
  { type: "expected_value", category: "portfolio" },
  { type: "asset_class_forecast", category: "portfolio" },
  { type: "volatility_chart", category: "portfolio" },
  { type: "equity_share_chart", category: "portfolio" },
  { type: "mandate", category: "portfolio" },
  { type: "mandate_comparison", category: "portfolio" },
  // Costs components
  { type: "cost_table", category: "costs" },
  { type: "cost_chart", category: "costs" },
  { type: "portfolio_costs", category: "costs" },
  { type: "product_costs", category: "costs" },
  { type: "cost_benefit", category: "costs" },
  // Client components
  { type: "client_classification", category: "client" },
  { type: "product_assessment", category: "client" },
  { type: "financial_situation", category: "client" },
  // QL-207 — the client's risk profile, placeable without a collection; under Portfolio it
  // kept that heading alive on flows that have no portfolios.
  { type: "risk_assessment", category: "client" },
  { type: "account_cash_overview", category: "client" },
  { type: "sustainability", category: "client" },
  { type: "client_preferences", category: "client" },
  { type: "quiz", category: "client" },
  { type: "sustainability_status", category: "portfolio" },
  { type: "portfolio_list", category: "portfolio" },
  { type: "wealth_plan", category: "client" },
  { type: "trapets_lookup", category: "client" },
  { type: "sumsub_verification", category: "client" },
  { type: "enin_lookup", category: "client" },
  // Formatting components
  { type: "text_block", category: "formatting" },
  { type: "spacer", category: "formatting" },
  { type: "youtube_video", category: "formatting" },
  { type: "iframe", category: "formatting" },
  { type: "image", category: "formatting" },
  { type: "embed", category: "formatting" },
  { type: "modal", category: "formatting" },
  { type: "file", category: "formatting" },
];

// Get localized component types
export function getFlowComponentTypes(lang: string): FlowComponentTypeInfo[] {
  const labels = pickT(COMPONENT_TYPE_LABELS, lang);

  return COMPONENT_METADATA.map((meta) => {
    const localized = labels[meta.type];
    return {
      ...meta,
      ...getComponentRequirements(meta.type),
      label: localized?.label || meta.type,
      description: localized?.description || "",
      detailedDescription: localized?.detailedDescription || "",
    };
  });
}

function getFlowComponentTypeInfo(
  type: ComponentType,
  lang = "en_GB",
): FlowComponentTypeInfo | undefined {
  const types = getFlowComponentTypes(lang);
  return types.find((ct) => ct.type === type);
}

export function getFlowComponentLabel(type: ComponentType, lang = "en_GB"): string {
  return getFlowComponentTypeInfo(type, lang)?.label || type;
}

/**
 * The label a component row shows in the page editor (page, two-column block
 * and tab block share it): a field's name, a text block's first heading
 * (QL-296 — it has no title), any other component's configured title, else
 * the localized type label.
 */
export function getFlowComponentRowLabel(
  component: FlowPageComponent & { flowField?: FlowField },
  lang = "en_GB",
): string {
  if (component.componentType === "field" && component.flowField) {
    return component.flowField.name;
  }
  try {
    const config = component.config ? JSON.parse(component.config) : null;
    if (component.componentType === "text_block") {
      const heading = textBlockHeading(config ?? {});
      if (heading) return heading;
    }
    if (config?.title) return config.title;
  } catch {
    // Unparseable config: fall through to the type label
  }
  return getFlowComponentLabel(component.componentType, lang);
}

// Single source of truth for component placement (QP-150): delegates to the shared
// placement policy so the editor (what can be added), the server-side validation
// (authoritative), and the runtime renderers can never disagree.
export function isComponentAllowedOnPageType(type: ComponentType, pageType: PageType): boolean {
  return isComponentAllowedOnPage(type, pageType);
}

const ALL_PAGE_TYPES: PageType[] = ["global", "goal", "portfolio"];

/** The page types a component may be placed on, derived from the shared policy. */
export function allowedPageTypesFor(type: ComponentType): PageType[] {
  return ALL_PAGE_TYPES.filter((pt) => isComponentAllowedOnPage(type, pt));
}
